import { ActionButton, ImageUploader } from '@emerald-works-nova/components'
import { Box, Button, Checkbox, Dialog, DialogTitle, Divider, FormControlLabel, Grid, IconButton, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import React from 'react'
import { useStyles } from './style'
import ImageDisplay from '../../image-display'

export const AddCategoryModal = React.memo(({
  open,
  isWorking,
  data = {},
  onCancel = () => { },
  onClose = () => { }
}) => {
  const classes = useStyles()
  const [entries, setEntries] = React.useState([{}])
  const { parentCat, type, startPosition } = data

  const recommendedCharCount = 600

  const getHelperText = (description) => {
    const helperText = description.length > recommendedCharCount
      ? `${description.length} | Exceeding ${recommendedCharCount} characters may result in unpredictable layout issues.`
      : `${description.length}`
    return helperText
  }

  const title = React.useMemo(() => {
    if (!type) return 'Group Category'
    return type === 'category' ? 'Category' : 'Sub-Category'
  }, [type])

  const addEntry = () => setEntries(entries => [...entries, {}])
  const rmEntry = idx => setEntries(entries => {
    entries.splice(idx, 1)
    return [...entries]
  })

  const handleChangeName = (idx, ev) => {
    setEntries(entries => {
      entries[idx].name = ev.target.value
      return [...entries]
    })
  }

  const handleChangeDescription = (idx, ev) => {
    setEntries(entries => {
      entries[idx].description = ev.target.value
      return [...entries]
    })
  }

  const handleChangeImageInfo = (idx, imageInfo) => {
    setEntries(entries => {
      entries[idx].imageInfo = imageInfo
      return [...entries]
    })
  }

  const handleChangeHideFromLPQ = (idx, ev) => {
    setEntries(entries => {
      entries[idx].hideFromLPQ = ev.target.checked
      return [...entries]
    })
  }

  const handleOnDropAccepted = (idx, imageInfo) => {
    setEntries(entries => {
      entries[idx].imageInfo = imageInfo
      return [...entries]
    })
  }

  const disabled = entries.some(entry => !entry.name)

  React.useEffect(() => { !open && setEntries([{}]) }, [open])

  return (
    <Dialog open={open} classes={{ paper: classes.dialogPaper }} aria-labelledby='manage-category-title'>
      <DialogTitle id='manage-category-title' className={classes.dialogTitle}>
        <Grid container className={classes.actionBtnCtn}>
          <Grid item xs={8}>Add New {title}</Grid>
          <Grid container item xs={4} justifyContent='flex-end'>
            <Button
              color='primary'
              data-test='cancel-button'
              className={classes.cancelBtn}
              onClick={onCancel}
            > Cancel
            </Button>
            <ActionButton
              variant='primary'
              dataTest='save-button'
              className={classes.saveBtn}
              onClick={() => onClose({ entries, parentCat, startPosition })}
              isWorking={isWorking}
              disabled={disabled}
            >
              Create
            </ActionButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <Box display='flex' flexDirection='column'>
        <Typography variant='subtitle1'>Template /</Typography>
        {entries.map((entry, idx) => (
          <Box key={idx}>
            {idx >= 1 && <Divider className={classes.input} />}
            <TextField
              fullWidth
              value={entry.name}
              className={classes.input}
              variant='outlined'
              placeholder='Add title'
              label='Title'
              onChange={(e) => handleChangeName(idx, e)}
            />
            <Box>
              <TextField
                fullWidth
                multiline
                value={entry.description}
                className={classes.input}
                variant='outlined'
                placeholder='Add description'
                label='Description'
                onChange={(e) => handleChangeDescription(idx, e)}
                helperText={
                  <div className={classes.helperText}>
                    <strong>Characters: </strong> {getHelperText(entry.description || '')}
                  </div>
                }
              />
              <Box className={classes.input}>
                {!entry.imageInfo?.imgUrl ? (
                  <ImageUploader
                    onDropAccepted={(e) => handleOnDropAccepted(idx, e)}
                    imageNamePrefix={entry.name}
                    validation={{ minWidth: 392, minHeight: 220 }}
                  />
                ) : (
                  <ImageDisplay
                    imageInfo={entry.imageInfo}
                    setImageInfo={(e) => handleChangeImageInfo(idx, e)}
                  />
                )}
              </Box>
            </Box>
            <Box marginBottom={3} display='flex' flexDirection='column' data-test='category-hide-from-lpq-container'>
              <FormControlLabel
                control={
                  <Checkbox
                    value={entry.hideFromLPQ}
                    checked={entry.hideFromLPQ}
                    onChange={(e) => handleChangeHideFromLPQ(idx, e)}
                    data-test='category-hide-from-lpq-checkbox'
                  />
                }
                label='Hide from LPQ'
              />
            </Box>
            {idx >= 1 && (
              <Box display='flex' flexDirection='row-reverse'>
                <IconButton onClick={() => rmEntry(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <Box mt={3} mb={2}>
        <ActionButton
          variant='primary'
          dataTest='add-new-button'
          className={classes.saveBtn}
          onClick={addEntry}
          disabled={disabled}
        >
          Add new
        </ActionButton>
      </Box>
    </Dialog>
  )
})
